/**
 * ステップ見積もりのステップナビゲーション（ウィザード）。
 *
 * 項目表から構築したステップ列を 1 画面 1 ステップで進む:
 *  - 選択肢グループ → 通知/ロール設定と同じ RadioCard の縦ブロック
 *    （左にオプションサムネイル、右上にラジオ、数量変更もブロック内）
 *  - 2 択かつ一方 0 円 → 1 ブロック内のトグル
 *  - 数量可変の単独項目 → 確認ブロック（常に見積もりへ含まれる）
 *  - 数量可変の行 → 選択中ブロック右下に浮かせた NumberStepper
 *    （範囲・初期値は表の設定。絶対配置なので選択でカード高さが変わらない）
 *  - 価格は Apple の構成カスタマイズと同様、未選択の選択肢にのみ
 *    「現在の合計（税込）からの差分（+/−）」を表示する（単価は表示しない）
 *  - 項目説明・項目サムネはステップ全体の表示、オプションテキスト・
 *    オプションサムネは各選択肢ブロック内の表示
 *  - 選択肢ブロックはステップ切替時に上から順にスライド + フェードイン
 *    （soremo.jp/mgk と同じ流儀の stagger アニメーション）
 *
 * 選択肢が無く数量も固定の項目は「問うことが何も無い」ためステップにはせず、
 * 最終確認の明細にのみ載せる（isWizardStep）。そのため明細の組み立てには
 * 常に全ステップ（allSteps）を使い、画面遷移だけ絞り込んだ steps を使う。
 *
 * 操作系は BottomAppBar（画面下固定バー）に統一する: 右に「次へ / PDF を作成」、
 * 左に「戻る」（ステップ 1 では表編集へ戻る = onClose）。バーには現在の合計
 * （税込）もライブ表示する。独自ヘッダーは持たず、GlobalHeader を残したまま
 * その下（top: 64px 以降）を全面白背景で覆う。
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import toast from "react-hot-toast";
import dayjs from "dayjs";

import StepFlowLayout from "../../../../components/step-flow/StepFlowLayout";
import NumberStepper from "../../../../components/form-item/NumberStepper";
import RadioCard from "../../../../components/form-item/RadioCard";
import Toggle from "../../../../components/form-item/Toggle";
import Stepper from "../../../../components/stepper/Stepper";
import {
  formatPdfExportError,
  requestStepEstimatePdf,
} from "../../../../app/services/pdfExports";
import { triggerBlobDownload } from "../../../../utils/downloadFile";
import {
  buildDefaultSelection,
  buildEstimateLines,
  buildSteps,
  calcTotals,
  clampQuantity,
  formatPrice,
  initialQuantityOf,
  isWizardStep,
  parseRowName,
  resolveUnitPrice,
  selectedRowOf,
  type EstimateItemRow,
  type EstimateStep,
  type WizardSelectionState,
} from "../utils/estimateModel";
import type { EstimateIssuer } from "../utils/presetPayload";

// ===== スタイル =====

/**
 * 本文（StepFlowLayout の読み幅コンテナ内）。
 * 選択肢カードのスライドイン（translateX）で横スクロールが出ないようにクリップする。
 */
const BodyInner = styled.div`
  width: 100%;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StepThumb = styled.img`
  width: 100%;
  max-height: 240px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--soremo-border);
`;

const OptionThumb = styled.img`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--soremo-border);
`;

/** 単独項目 / トグル項目の確認ブロック（RadioCard と同じ面構成の枠）。 */
const PlainCard = styled.div`
  width: 100%;
  padding: 16px 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fff;
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);
`;

/**
 * 選択肢ブロックの出現アニメーション（soremo.jp/mgk の
 * gsap.from(opacity: 0, x: -128, duration: 0.5, stagger: 0.2) を CSS で再現）。
 */
const choiceAppear = keyframes`
  from {
    opacity: 0;
    transform: translateX(-128px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

/**
 * 上のブロックから順（0.2s 間隔）にスライド + フェードインさせるラッパー。
 * 親（.u-col = align-items: flex-start）で縮まないよう全幅を明示し、
 * ブロック幅を通知カードと同じくコンテナ全幅で揃える。
 */
const AnimatedChoice = styled.div<{ $index: number }>`
  width: 100%;
  opacity: 0;
  animation: ${choiceAppear} 0.5s ease-out forwards;
  animation-delay: ${({ $index }) => $index * 0.2}s;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

/** 確認ブロック（PlainCard）内の数量変更行。上に区切り線を入れる。 */
const QuantityRow = styled.div.attrs({
  className: "u-row u-gap8 u-align-center",
})`
  padding-top: 10px;
  border-top: 1px solid var(--soremo-border);
`;

/** 選択肢カードの高さを確保するぶんの余白（FloatingQuantity と揃える）。 */
const FLOATING_QUANTITY_SPACE_PX = 40;

/**
 * 選択肢カード内の数量変更。カード右下に絶対配置で浮かせることで、
 * 選択 / 未選択でカードの高さが変わらないようにする。
 */
const FloatingQuantity = styled.div.attrs({
  className: "u-row u-gap8 u-align-center",
})`
  position: absolute;
  right: 0;
  bottom: 0;
  padding: 4px 8px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fff;
`;

/**
 * 選択肢カードの中身。FloatingQuantity の絶対配置の基準になる。
 * 数量可変の行は選択状態に関わらず下端に余白を確保しておき、
 * ステッパーの出し入れでレイアウトが動かないようにする。
 */
const ChoiceCardBody = styled.div.attrs({ className: "u-col u-gap8 u-w100" })<{
  $hasThumb: boolean;
  $reserveQuantity: boolean;
}>`
  position: relative;
  padding-right: 24px;
  padding-left: ${({ $hasThumb }) => ($hasThumb ? 12 : 0)}px;
  padding-bottom: ${({ $reserveQuantity }) =>
    $reserveQuantity ? FLOATING_QUANTITY_SPACE_PX : 0}px;
`;

const SummaryTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 8px;
    border-bottom: 1px solid var(--soremo-border);
    text-align: left;
    vertical-align: top;
  }

  th {
    background: var(--soremo-background);
    white-space: nowrap;
  }

  td.num,
  th.num {
    text-align: right;
    white-space: nowrap;
  }

  tfoot td {
    border-bottom: none;
  }
`;

/** 明細の「選択肢が無く常に含まれる項目」を示すチップ。 */
const FixedChip = styled.span.attrs({ className: "bodytext-11" })`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  border: 1px solid var(--soremo-border);
  border-radius: 10px;
  color: var(--soremo-gray);
  white-space: nowrap;
`;

/** 確認画面の宛名・件名（PDF に載る内容の読み取り専用表示）。 */
const DocumentMetaLabel = styled.span.attrs({ className: "bodytext-13" })`
  color: var(--soremo-gray);
  white-space: nowrap;
`;

// ===== 補助 =====

/** 選択肢カードの見出し（備考があれば備考、なければ「選択肢 A」）。 */
const optionTitleOf = (row: EstimateItemRow): string => {
  if (row.note.trim()) return row.note.trim();
  const { suffix } = parseRowName(row.name);
  return suffix ? `選択肢 ${suffix.toUpperCase()}` : row.name;
};

/** 現在の合計からの差分表示（+¥66,000 / −¥110,000 / ±¥0）。 */
const formatSignedDiff = (diff: number): string => {
  if (diff === 0) return `±${formatPrice(0)}`;
  return `${diff > 0 ? "+" : "−"}${formatPrice(Math.abs(diff))}`;
};

// ===== 本体 =====

export interface EstimateWizardProps {
  rows: EstimateItemRow[];
  /** 御見積書の件名（表編集画面で入力・プリセットと一緒に保存される）。 */
  subject: string;
  /** 御見積書の宛名（表編集画面で入力・プリセットと一緒に保存される）。 */
  customerName: string;
  /** 御見積書の発行者欄（表編集画面で入力・プリセットと一緒に保存される）。 */
  issuer: EstimateIssuer;
  onClose: () => void;
}

const EstimateWizard: React.FC<EstimateWizardProps> = ({
  rows,
  subject,
  customerName,
  issuer,
  onClose,
}) => {
  /** 明細の組み立てに使う全ステップ（固定項目を含む）。 */
  const allSteps = useMemo(() => buildSteps(rows), [rows]);
  /** 画面遷移に使うステップ（ユーザーに問うものだけ）。 */
  const steps = useMemo(() => allSteps.filter(isWizardStep), [allSteps]);

  const [selection, setSelection] = useState<WizardSelectionState>(() =>
    buildDefaultSelection(allSteps),
  );
  /** 0..steps.length-1 が各ステップ、steps.length が確認ステップ。 */
  const [stepIndex, setStepIndex] = useState(0);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const isSummary = stepIndex >= steps.length;
  const currentStep: EstimateStep | null = isSummary ? null : steps[stepIndex];

  const lines = useMemo(
    () => buildEstimateLines(allSteps, rows, selection),
    [allSteps, rows, selection],
  );
  const totals = useMemo(() => calcTotals(lines), [lines]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const selectRow = useCallback((step: EstimateStep, rowId: string) => {
    setSelection((prev) => ({
      ...prev,
      selected: { ...prev.selected, [step.key]: rowId },
    }));
  }, []);

  const setQuantity = useCallback((row: EstimateItemRow, value: number) => {
    setSelection((prev) => ({
      ...prev,
      quantities: { ...prev.quantities, [row.id]: clampQuantity(row, value) },
    }));
  }, []);

  const handleCreatePdf = useCallback(async () => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    try {
      const { blob, filename } = await requestStepEstimatePdf({
        subject: subject.trim() || undefined,
        customerName: customerName.trim() || undefined,
        issuer,
        lines: lines.map((line) => ({
          name: line.name,
          price: line.price,
          quantity: line.quantity,
          unit: line.unit,
          note: line.note,
        })),
      });
      // 通常は BE の Content-Disposition のファイル名を使う（これはフォールバック）
      const yymmdd = dayjs().format("YYMMDD");
      triggerBlobDownload(blob, filename || `御見積書_超松竹梅_${yymmdd}.pdf`);
      toast.success("御見積書 PDF を書き出しました");
    } catch (e) {
      toast.error(formatPdfExportError(e));
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [isGeneratingPdf, subject, customerName, issuer, lines]);

  /** 数量ステッパーの中身（表示形態は呼び出し側のラッパーで変える）。 */
  const renderQuantityControls = (row: EstimateItemRow) => {
    const value = clampQuantity(
      row,
      selection.quantities[row.id] ?? initialQuantityOf(row),
    );
    const min = Math.min(row.quantityMin, row.quantityMax);
    const max = Math.max(row.quantityMin, row.quantityMax);
    return (
      <>
        <span className="bodytext-13">数量</span>
        <NumberStepper
          value={value}
          min={min}
          max={max}
          onChange={(v) => setQuantity(row, v)}
          aria-label={`${optionTitleOf(row)}の数量`}
        />
        {row.unit && <span className="bodytext-13">{row.unit}</span>}
      </>
    );
  };

  /** 確認ブロック（PlainCard）内の数量変更行。 */
  const renderInlineQuantity = (row: EstimateItemRow) => {
    if (!row.quantityVariable) return null;
    return <QuantityRow>{renderQuantityControls(row)}</QuantityRow>;
  };

  /** 選択肢カード右下に浮かせる数量変更。 */
  const renderFloatingQuantity = (row: EstimateItemRow) => {
    if (!row.quantityVariable) return null;
    return <FloatingQuantity>{renderQuantityControls(row)}</FloatingQuantity>;
  };

  /**
   * 選択を row に切り替えた場合の税込合計と現在の合計の差分。
   * 選択中の行は null（価格エリア非表示）。
   */
  const diffFromCurrent = (step: EstimateStep, rowId: string): number | null => {
    const selectedRow = selectedRowOf(step, selection);
    if (selectedRow.id === rowId) return null;
    const altSelection: WizardSelectionState = {
      ...selection,
      selected: { ...selection.selected, [step.key]: rowId },
    };
    const altTotal = calcTotals(
      buildEstimateLines(allSteps, rows, altSelection),
    ).total;
    return altTotal - totals.total;
  };

  /**
   * 価格表示。未選択の行にのみ、現在の合計（税込）からの差分
   * （Apple の構成カスタマイズと同じ流儀）を表示する。
   * 選択中の行・単独項目の単価は表示しない（フッターの合計と
   * 確認ステップの明細で確認できる）。テキスト価格（「別途お見積り」等）は
   * 金額でないためそのまま表示する。
   */
  const renderPriceArea = (step: EstimateStep, row: EstimateItemRow) => {
    const resolved = resolveUnitPrice(row, rows, selection);
    if (resolved.kind === "text") {
      return (
        <div className="u-col u-gap4" style={{ alignItems: "flex-end" }}>
          <span className="heading-16">{resolved.text}</span>
        </div>
      );
    }
    const diff = diffFromCurrent(step, row.id);
    if (diff === null) return null;
    return (
      <div className="u-col u-gap4" style={{ alignItems: "flex-end" }}>
        <span className="heading-16">{formatSignedDiff(diff)}</span>
      </div>
    );
  };

  /**
   * ブロック内の共通コンテンツ行（サムネ・タイトル・説明・価格）。
   * タイポグラフィはマイページ通知のカード（label + bodytext-13）に合わせる。
   */
  const renderBlockContentRow = (
    step: EstimateStep,
    row: EstimateItemRow,
    showThumb = true,
  ) => (
    <div className="u-row u-gap12 u-w100" style={{ alignItems: "flex-start" }}>
      {showThumb && row.optionThumbnailUrl && (
        <OptionThumb src={row.optionThumbnailUrl} alt={optionTitleOf(row)} />
      )}
      <div className="u-col u-gap4" style={{ flex: 1, minWidth: 0 }}>
        <label>{optionTitleOf(row)}</label>
        {row.optionText && (
          <p className="bodytext-13">{row.optionText}</p>
        )}
      </div>
      {renderPriceArea(step, row)}
    </div>
  );

  const renderChoiceStep = (step: EstimateStep) => {
    const selectedRow = selectedRowOf(step, selection);

    if (step.isToggle && step.toggleOnRow && step.toggleOffRow) {
      const onRow = step.toggleOnRow;
      const offRow = step.toggleOffRow;
      const isOn = selectedRow.id === onRow.id;
      return (
        <AnimatedChoice $index={0}>
          <PlainCard>
            <div className="u-col u-gap12 u-w100">
              <div
                className="u-w100"
                style={{ opacity: isOn ? 1 : 0.45, transition: "opacity 0.2s ease" }}
              >
                {renderBlockContentRow(step, onRow)}
              </div>
              <Toggle
                value={isOn}
                onClick={() => selectRow(step, isOn ? offRow.id : onRow.id)}
              >
                <span className="bodytext-13">
                  {isOn ? "見積もりに含める" : "見積もりに含めない"}
                </span>
              </Toggle>
              {isOn && renderInlineQuantity(onRow)}
            </div>
          </PlainCard>
        </AnimatedChoice>
      );
    }

    return (
      <div className="u-col u-gap16 u-w100">
        {step.rows.map((row, rowIndex) => {
          const isSelected = selectedRow.id === row.id;
          const hasThumb = Boolean(row.optionThumbnailUrl);
          return (
            <AnimatedChoice key={row.id} $index={rowIndex}>
              <RadioCard
                checked={isSelected}
                height="auto"
                width={hasThumb ? "128px" : "0px"}
                imageUrl={row.optionThumbnailUrl || undefined}
                imageShape="square"
                /* 通知カードと同じ高さ。数量ステッパーは右下に浮かせるので
                   選択してもカードは下に伸びない */
                style={{ minHeight: 128 }}
                onClick={() => selectRow(step, row.id)}
              >
                <ChoiceCardBody
                  $hasThumb={hasThumb}
                  $reserveQuantity={row.quantityVariable}
                >
                  {/* サムネは RadioCard 側（imageUrl）で表示するため showThumb=false */}
                  {renderBlockContentRow(step, row, false)}
                  {isSelected && renderFloatingQuantity(row)}
                </ChoiceCardBody>
              </RadioCard>
            </AnimatedChoice>
          );
        })}
      </div>
    );
  };

  /**
   * 選択肢は無いが数量可変なのでユーザーに問う必要がある項目のステップ。
   * 数量も固定の項目は isWizardStep で除外済みなのでここには来ない。
   */
  const renderFixedStep = (step: EstimateStep) => {
    const row = step.rows[0];
    return (
      <div className="u-col u-gap12 u-w100">
        <AnimatedChoice $index={0}>
          <PlainCard>
            <div className="u-col u-gap12 u-w100">
              {renderBlockContentRow(step, row)}
              {renderInlineQuantity(row)}
            </div>
          </PlainCard>
        </AnimatedChoice>
        <span className="bodytext-11" style={{ color: "var(--soremo-gray)" }}>
          この項目は見積もりに含まれます。数量だけ選んでください。
        </span>
      </div>
    );
  };

  const hasFixedLine = lines.some((line) => line.isFixed);

  const renderSummary = () => (
    <div className="u-col u-gap16">
      <h2 className="heading-16">お見積もり内容の確認</h2>

      {/* 宛名・件名は表編集画面で入力済み（プリセットと一緒に保存）。
        * 御見積書と同じ「宛名 → 件名 → 明細」の順で最終確認する */}
      <div className="u-col u-gap8">
        <div className="u-row u-gap8">
          <DocumentMetaLabel>宛名</DocumentMetaLabel>
          <span className="bodytext-13">
            {customerName.trim() ? `${customerName.trim()} 御中` : "（未入力）"}
          </span>
        </div>
        <div className="u-row u-gap8">
          <DocumentMetaLabel>件名</DocumentMetaLabel>
          <span className="bodytext-13">
            {subject.trim() || "（未入力）"}
          </span>
        </div>
        <span className="bodytext-11" style={{ color: "var(--soremo-gray)" }}>
          宛名・件名は表編集画面で変更できます。
        </span>
      </div>

      <SummaryTable>
        <thead>
          <tr>
            <th className="bodytext-11">内容</th>
            <th className="bodytext-11 num">単価</th>
            <th className="bodytext-11 num">数量</th>
            <th className="bodytext-11 num">金額</th>
            <th className="bodytext-11">備考</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.rowId}>
              <td className="bodytext-13">
                {line.name}
                {line.isFixed && <FixedChip>標準で含まれます</FixedChip>}
              </td>
              <td className="bodytext-13 num">{formatPrice(line.price)}</td>
              <td className="bodytext-13 num">
                {line.quantity}
                {line.unit && (
                  <span className="bodytext-11 u-pl4">{line.unit}</span>
                )}
              </td>
              <td className="bodytext-13 num">
                {line.amount === null ? "—" : formatPrice(line.amount)}
              </td>
              <td className="bodytext-11">{line.note}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} className="bodytext-13">
              課税対象額
            </td>
            <td className="bodytext-13 num">{formatPrice(totals.subtotal)}</td>
            <td />
          </tr>
          <tr>
            <td colSpan={3} className="bodytext-13">
              消費税（10%）
            </td>
            <td className="bodytext-13 num">{formatPrice(totals.tax)}</td>
            <td />
          </tr>
          <tr>
            <td colSpan={3} className="heading-13">
              合計
            </td>
            <td className="heading-13 num">{formatPrice(totals.total)}</td>
            <td />
          </tr>
        </tfoot>
      </SummaryTable>

      {hasFixedLine && (
        <span className="bodytext-11" style={{ color: "var(--soremo-gray)" }}>
          ※「標準で含まれます」の項目は選択肢が無いため、ステップでは確認せず常に見積もりへ含めています。
        </span>
      )}
      {totals.hasTextPrice && (
        <span className="bodytext-11" style={{ color: "var(--soremo-gray)" }}>
          ※ 金額欄が「—」の項目は計算外です（別途お見積り等）。
        </span>
      )}
    </div>
  );

  const stepLabels = useMemo(
    () => [...steps.map((s) => s.title), "確認"],
    [steps],
  );

  const goBack = () =>
    stepIndex === 0 ? onClose() : setStepIndex((i) => Math.max(0, i - 1));

  return (
    <StepFlowLayout
      ariaLabel="ステップ見積もり"
      onBack={onClose}
      stepperWidth="wide"
      stepper={
        allSteps.length > 0 ? (
          <Stepper
            stepLabels={stepLabels}
            current={stepIndex + 1}
            showCheckOnComplete
            onSelectStep={(n) => setStepIndex(n - 1)}
            isStepClickable={(n) => n - 1 <= stepIndex}
          />
        ) : undefined
      }
      // ステップ 1 の「戻る」は表編集へ戻る
      secondaryAction={{
        label: "戻る",
        onClick: goBack,
        leftIcon: "chevron_left",
      }}
      primaryAction={{
        label: isSummary ? (isGeneratingPdf ? "生成中…" : "PDF を作成") : "次へ",
        rightIcon: isSummary ? "picture_as_pdf" : "chevron_right",
        disabled: isSummary && (isGeneratingPdf || lines.length === 0),
        onClick: () => (isSummary ? handleCreatePdf() : setStepIndex((i) => i + 1)),
      }}
      footerCenter={
        <div className="u-col u-gap4 u-items-end">
          <span className="bodytext-11 u-text-gray">
            現在の合計（税込）
            {totals.hasTextPrice && " ※計算外の項目あり"}
          </span>
          <span className="heading-16">{formatPrice(totals.total)}</span>
        </div>
      }
    >
      <BodyInner>
        {allSteps.length === 0 ? (
          <span className="bodytext-13">
            項目がありません。表に項目を追加してください。
          </span>
        ) : currentStep ? (
          // key でステップ切替ごとに再マウントし、選択肢の
          // スライドインアニメーションを毎回先頭から再生する
          <div className="u-col u-gap12" key={currentStep.key}>
            <h2 className="heading-16">{currentStep.title}</h2>
            {currentStep.description && (
              <p className="bodytext-13">{currentStep.description}</p>
            )}
            {currentStep.thumbnailUrl && (
              <StepThumb
                src={currentStep.thumbnailUrl}
                alt={currentStep.title}
              />
            )}
            {currentStep.isChoice
              ? renderChoiceStep(currentStep)
              : renderFixedStep(currentStep)}
          </div>
        ) : (
          renderSummary()
        )}
      </BodyInner>
    </StepFlowLayout>
  );
};

export default EstimateWizard;
